/*
 * hardware id detection utility
 * generates a unique hardware fingerprint for the client machine
 * uses multiple browser/system characteristics for reliable identification
 */

interface HardwareFingerprint {
  hardwareId: string
  machineName: string
  deviceName: string
  components: {
    userAgent: string
    platform: string
    language: string
    screenResolution: string
    timezone: string
    cores: number
    memory: number
    gpu: string
  }
}

/*
 * generate a unique hardware id based on browser fingerprinting
 * this creates a consistent id for the same machine across sessions
 */
export async function getHardwareFingerprint(): Promise<HardwareFingerprint> {
  const components = {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screenResolution: `${screen.width}x${screen.height}x${screen.colorDepth}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cores: navigator.hardwareConcurrency || 0,
    memory: (navigator as any).deviceMemory || 0,
    gpu: await getGPUInfo()
  }

  // create a unique fingerprint string
  const fingerprintString = [
    components.userAgent,
    components.platform,
    components.screenResolution,
    components.timezone,
    components.cores.toString(),
    components.memory.toString(),
    components.gpu
  ].join('|')

  // generate hash from fingerprint
  const hardwareId = await generateHash(fingerprintString)

  // extract machine/device names
  const machineName = extractMachineName(components.userAgent)
  const deviceName = extractDeviceName(components.userAgent, components.platform)

  return {
    hardwareId,
    machineName,
    deviceName,
    components
  }
}

/* get gpu information */
async function getGPUInfo(): Promise<string> {
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
    
    if (gl) {
      const debugInfo = (gl as any).getExtension('WEBGL_debug_renderer_info')
      if (debugInfo) {
        return (gl as any).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'Unknown'
      }
    }
    return 'Unknown'
  } catch (e) {
    return 'Unknown'
  }
}

/*
 * generate sha-256 hash from string
 * always returns uppercase to ensure consistency across systems
 */
async function generateHash(input: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(input)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  // ensure uppercase for consistency (matches database and other program expectations)
  return hashHex.substring(0, 32).toUpperCase() // use first 32 chars for readability
}

/* extract machine name from user agent */
function extractMachineName(userAgent: string): string {
  // try to extract os info
  if (userAgent.includes('Windows')) return 'Windows PC'
  if (userAgent.includes('Macintosh')) return 'Mac'
  if (userAgent.includes('Linux')) return 'Linux Machine'
  if (userAgent.includes('Android')) return 'Android Device'
  if (userAgent.includes('iPhone')) return 'iPhone'
  if (userAgent.includes('iPad')) return 'iPad'
  return 'Unknown Device'
}

/* extract device name with more details */
function extractDeviceName(userAgent: string, platform: string): string {
  const parts = []
  
  // os
  if (userAgent.includes('Windows NT 10.0')) parts.push('Windows 10/11')
  else if (userAgent.includes('Windows NT 6.3')) parts.push('Windows 8.1')
  else if (userAgent.includes('Windows NT 6.2')) parts.push('Windows 8')
  else if (userAgent.includes('Windows NT 6.1')) parts.push('Windows 7')
  else if (userAgent.includes('Mac OS X')) {
    const match = userAgent.match(/Mac OS X ([\d_]+)/)
    parts.push(match ? `macOS ${match[1].replace(/_/g, '.')}` : 'macOS')
  }
  else if (userAgent.includes('Linux')) parts.push('Linux')
  else if (userAgent.includes('Android')) {
    const match = userAgent.match(/Android ([\d.]+)/)
    parts.push(match ? `Android ${match[1]}` : 'Android')
  }
  else if (userAgent.includes('iPhone OS')) {
    const match = userAgent.match(/iPhone OS ([\d_]+)/)
    parts.push(match ? `iOS ${match[1].replace(/_/g, '.')}` : 'iOS')
  }
  
  // browser
  if (userAgent.includes('Edg/')) parts.push('Edge')
  else if (userAgent.includes('Chrome/')) parts.push('Chrome')
  else if (userAgent.includes('Firefox/')) parts.push('Firefox')
  else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome')) parts.push('Safari')

  return parts.length > 0 ? parts.join(' • ') : platform
}

/* get current ip address (approximate) */
export async function getCurrentIP(): Promise<string> {
  try {
    const response = await fetch('https://api.ipify.org?format=json')
    const data = await response.json()
    return data.ip || 'Unknown'
  } catch (e) {
    return 'Unknown'
  }
}

